import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, CircleDashed, Loader2, X } from "lucide-react";
import { formatAccountNo, useAccounts } from "@/lib/accounts";
import { ApiError } from "@/lib/api";
import { PUBLISHER, useAddChannel, useBots, useCreateBot, useOwnChannels } from "@/lib/aggregator";
import { LIMITS, useCreateOwnChat } from "@/lib/ownChats";
import { Choice, UsernameField } from "@/components/ownchats/CreateOwnChatDialog";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { cn } from "@/lib/cn";
import { AccountSelect } from "./AccountSelect";
import { errorText } from "./helpers";

type Step = "channel" | "bot" | "admin";

/**
 * A channel for the aggregator to fill — a new one, or one an account of the
 * company already runs. Everything Telegram has to be asked for is done by the
 * accounts, one step after another, each shown: the channel is created, the
 * bot is made at @BotFather if the company has none, the bot becomes the
 * channel's admin. A step that failed is the one "Continue" begins with: what
 * was made before it is kept, not made again.
 */
export function AddChannelDialog({
  open,
  onOpenChange,
  onAdded,
  canManage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: (id: string) => void;
  /** May make a bot — the company's admin. */
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const bots = useBots();
  const accounts = useAccounts();
  const own = useOwnChannels(open);
  const createChat = useCreateOwnChat();
  const createBot = useCreateBot();
  const add = useAddChannel();

  const [mode, setMode] = useState<"new" | "own">("new");
  const [title, setTitle] = useState("");
  const [account, setAccount] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [riskAccepted, setRiskAccepted] = useState(false);
  const [picked, setPicked] = useState("");
  const [botId, setBotId] = useState("");
  const [botAccount, setBotAccount] = useState("");
  // What the steps already made: kept across a failure, so that "Continue"
  // does not create a second channel or a second bot.
  const [made, setMade] = useState<{ chat: string; title: string } | null>(null);
  const [newBot, setNewBot] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [running, setRunning] = useState<Step | null>(null);
  const [failed, setFailed] = useState<Step | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The second click of a double click lands before the button is redrawn as
  // busy: without this the steps would be walked twice, side by side.
  const underWay = useRef(false);

  // Every opening starts clean.
  useEffect(() => {
    if (!open) return;
    setMode("new");
    setTitle("");
    setAccount("");
    setIsOpen(false);
    setUsername("");
    setRiskAccepted(false);
    setPicked("");
    setBotId("");
    setBotAccount("");
    setMade(null);
    setNewBot(null);
    setNote(null);
    setRunning(null);
    setFailed(null);
    setError(null);
  }, [open]);

  const usableBots = useMemo(
    () => (bots.data ?? []).filter((bot) => bot.problem === null),
    [bots.data],
  );
  const bot = usableBots.find((b) => b.id === botId) ?? usableBots[0];
  // The account that runs the channel publishes: no bot is asked for.
  const byAccount = PUBLISHER === "account";
  const needsBot = !byAccount && bots.data !== undefined && usableBots.length === 0;
  const free = (own.data ?? []).filter((c) => !c.connected);
  const chosen = accounts.data?.find((a) => a.id === account);
  const name = username.trim().replace(/^@/, "");
  const busy = running !== null;

  const channelReady =
    made !== null ||
    (mode === "own"
      ? picked !== ""
      : title.trim() !== "" &&
        chosen !== undefined &&
        (!isOpen || name !== "") &&
        (chosen.warmup_completed || riskAccepted));
  const ready =
    channelReady &&
    (byAccount ||
      // With the bots not read yet nobody knows whether one has to be made:
      // begun now, the run would make the channel and stop for good at the bot.
      (bots.data !== undefined &&
        (!needsBot || newBot !== null || (canManage && botAccount !== ""))));

  const steps: Step[] = [
    ...(mode === "new" ? (["channel"] as Step[]) : []),
    // Once a run has made the bot the step stays in sight, done.
    ...(needsBot || newBot !== null ? (["bot"] as Step[]) : []),
    "admin",
  ];
  const stateOf = (step: Step): "done" | "running" | "failed" | "waiting" => {
    if (running === step) return "running";
    if (failed === step) return "failed";
    if (step === "channel") return made !== null ? "done" : "waiting";
    if (step === "bot") return newBot !== null ? "done" : "waiting";
    return "waiting";
  };

  const submit = async () => {
    if (underWay.current) return;
    underWay.current = true;
    setError(null);
    setFailed(null);
    let step: Step = "channel";
    let chat = made?.chat ?? (mode === "own" ? picked : "");
    try {
      if (!chat) {
        setRunning("channel");
        const created = await createChat.mutateAsync({
          account_id: account,
          kind: "channel",
          title: title.trim(),
          about: "",
          username: isOpen ? name : null,
        });
        chat = created.chat.id;
        setMade({ chat, title: created.chat.title });
        // Created, though a later step of its own did not go through — the
        // public name was taken, say. The channel is there, closed.
        setNote(created.warning);
      }
      let withBot = byAccount ? undefined : (newBot ?? bot?.id);
      if (!withBot && !byAccount) {
        step = "bot";
        setRunning("bot");
        withBot = (await createBot.mutateAsync(botAccount)).id;
        setNewBot(withBot);
      }
      step = "admin";
      setRunning("admin");
      const channel = await add.mutateAsync({ own_chat_id: chat, bot_id: withBot });
      pushToast({ kind: "success", text: t("aggregator.channel.added", { title: channel.title }) });
      onOpenChange(false);
      onAdded(channel.id);
    } catch (err) {
      // Whichever way it ended it is said here, in the dialog — not once more
      // in the app's toast for errors nobody showed.
      if (err instanceof ApiError) err.shown = true;
      // No answer at all (the line dropped, the server restarted) is not
      // "nothing happened".
      const silent = !(err instanceof ApiError) || err.status === 0;
      if (step === "channel" && silent) {
        // The channel may have been created: a second press on "new" would
        // create another. The list of the accounts' own channels is read
        // again and shown — if it is there, it is taken from there.
        setError(t("aggregator.channel.no_answer_channel"));
        setMode("own");
        void own.refetch();
        return;
      }
      if (
        step === "admin" &&
        err instanceof ApiError &&
        err.status === 409 &&
        err.detail.includes("уже добавлен")
      ) {
        // "Already added" (the server's own words for it — other refusals
        // answer 409 too): the request before this one went through, and
        // only its answer was lost.
        pushToast({ kind: "success", text: t("aggregator.channel.already_added") });
        onOpenChange(false);
        return;
      }
      setFailed(step);
      setError(silent ? t("aggregator.channel.no_answer") : errorText(err, t("aggregator.channel.no_answer")));
    } finally {
      underWay.current = false;
      setRunning(null);
    }
  };

  const started = busy || failed !== null || made !== null;
  // The form may be scrolled to its end, where the button is: the steps — and
  // a step's failure — are brought into sight.
  const stepsRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    if (started) stepsRef.current?.scrollIntoView({ block: "nearest" });
  }, [started, running, failed]);

  return (
    <Dialog open={open} onOpenChange={(next) => (next || !busy) && onOpenChange(next)}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("aggregator.channel.add_title")}</DialogTitle>
          <DialogDescription>
            {t(byAccount ? "aggregator.channel.add_subtitle_account" : "aggregator.channel.add_subtitle")}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          {started && (
            <ol
              ref={stepsRef}
              className="space-y-1.5 rounded-xl border border-border bg-bg-primary px-4 py-3 text-sm">
              {steps.map((step) => {
                const state = stateOf(step);
                return (
                  <li
                    key={step}
                    data-step={step}
                    data-state={state}
                    className={cn(
                      "flex items-center gap-2",
                      state === "waiting" && "text-fg-muted",
                      state === "failed" && "text-danger",
                    )}
                  >
                    {state === "running" ? (
                      <Loader2 size={15} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                    ) : state === "done" ? (
                      <Check size={15} strokeWidth={2} className="text-success" aria-hidden="true" />
                    ) : state === "failed" ? (
                      <X size={15} strokeWidth={2} aria-hidden="true" />
                    ) : (
                      <CircleDashed size={15} strokeWidth={2} aria-hidden="true" />
                    )}
                    {t(
                      step === "admin" && byAccount
                        ? "aggregator.channel.step_connect"
                        : `aggregator.channel.step_${step}`,
                    )}
                  </li>
                );
              })}
            </ol>
          )}
          {error && <Alert tone="danger">{error}</Alert>}
          <div
            role="radiogroup"
            aria-label={t("aggregator.channel.add_title")}
            className={cn("flex flex-col gap-2 sm:flex-row", made && "pointer-events-none opacity-60")}
          >
            <Choice
              active={mode === "new"}
              onClick={() => !busy && setMode("new")}
              title={t("aggregator.channel.mode_new")}
              hint={t("aggregator.channel.mode_new_hint")}
            />
            <Choice
              active={mode === "own"}
              onClick={() => !busy && setMode("own")}
              title={t("aggregator.channel.mode_own")}
              hint={t("aggregator.channel.mode_own_hint")}
            />
          </div>

          {made ? (
            <Alert tone="info">
              <p>{t("aggregator.channel.created_note", { title: made.title })}</p>
              {note && <p className="mt-1">{note}</p>}
            </Alert>
          ) : mode === "new" ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="agg_new_title">{t("aggregator.channel.title")}</Label>
                <Input
                  id="agg_new_title"
                  value={title}
                  maxLength={LIMITS.title}
                  disabled={busy}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t("aggregator.channel.title_placeholder") ?? ""}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("aggregator.channel.account")}</Label>
                <AccountSelect value={account} onChange={setAccount} disabled={busy} />
                <p className="text-xs text-fg-muted">{t("aggregator.channel.account_hint")}</p>
                {chosen?.spam_state === "limited" && (
                  <Alert tone="warning">{t("aggregator.channel.account_limited")}</Alert>
                )}
              </div>
              <div className="space-y-2">
                <Label>{t("own_chats.field.access")}</Label>
                <div
                  role="radiogroup"
                  aria-label={t("own_chats.field.access")}
                  className="flex flex-col gap-2 sm:flex-row"
                >
                  <Choice
                    active={!isOpen}
                    onClick={() => !busy && setIsOpen(false)}
                    title={t("own_chats.access.closed")}
                    hint={t("own_chats.access.closed_hint")}
                  />
                  <Choice
                    active={isOpen}
                    onClick={() => !busy && setIsOpen(true)}
                    title={t("own_chats.access.open")}
                    hint={t("own_chats.access.open_hint")}
                  />
                </div>
                {isOpen && (
                  <UsernameField
                    id="agg_new_username"
                    value={username}
                    onChange={setUsername}
                    disabled={busy}
                  />
                )}
              </div>
              {chosen && !chosen.warmup_completed && (
                <Alert tone="warning" title={t("own_chats.create.risk_title")}>
                  <p>{t("own_chats.create.risk_text")}</p>
                  <label className="mt-2 flex cursor-pointer items-center gap-2 text-fg-primary">
                    <Checkbox
                      checked={riskAccepted}
                      onCheckedChange={(v) => setRiskAccepted(v === true)}
                    />
                    {t("own_chats.create.risk_accept")}
                  </label>
                </Alert>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <Label>{t("aggregator.channel.pick")}</Label>
              {own.data && free.length === 0 ? (
                <p className="text-sm text-fg-muted">{t("aggregator.channel.pick_empty")}</p>
              ) : (
                <SimpleSelect
                  value={picked}
                  onValueChange={setPicked}
                  disabled={busy || own.isLoading}
                  placeholder={t("aggregator.channel.pick_placeholder") ?? undefined}
                  options={free.map((c) => ({
                    value: c.id,
                    label: `${c.title} · ${
                      c.username ? `@${c.username}` : t("aggregator.card.private")
                    } · ${formatAccountNo(c.account_seq_no)}`,
                  }))}
                />
              )}
              <p className="text-xs leading-relaxed text-fg-muted">
                {t("aggregator.channel.pick_hint")}
              </p>
            </div>
          )}

          {byAccount ? (
            <p className="text-xs leading-relaxed text-fg-muted">
              {t("aggregator.channel.by_account_hint")}
            </p>
          ) : (
          <div className="space-y-2">
            <Label>{t("aggregator.channel.bot")}</Label>
            {newBot !== null ? (
              <p className="text-sm text-fg-secondary">{t("aggregator.channel.bot_made")}</p>
            ) : !needsBot ? (
              usableBots.length > 1 ? (
                <SimpleSelect
                  value={bot?.id ?? ""}
                  onValueChange={setBotId}
                  disabled={busy}
                  options={usableBots.map((b) => ({ value: b.id, label: `@${b.username}` }))}
                />
              ) : (
                <p className="text-sm text-fg-secondary">
                  {bot ? t("aggregator.channel.bot_is", { name: bot.username }) : "…"}
                </p>
              )
            ) : canManage ? (
              <>
                <p className="text-sm text-fg-secondary">{t("aggregator.channel.bot_new")}</p>
                <AccountSelect value={botAccount} onChange={setBotAccount} disabled={busy} />
                <p className="text-xs leading-relaxed text-fg-muted">{t("aggregator.bots.risk")}</p>
              </>
            ) : (
              <Alert tone="warning" title={t("aggregator.channel.no_bot_title")}>
                {t("aggregator.channel.no_bot_text")}
              </Alert>
            )}
          </div>
          )}

        </DialogBody>
        <DialogFooter>
          {busy && (
            <span className="mr-auto text-xs text-fg-muted">{t("aggregator.channel.working")}</span>
          )}
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} loading={busy} disabled={busy || !ready}>
            {t(failed ? "aggregator.channel.continue" : "aggregator.channel.add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
